import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Universal Chart Data Interface
interface ChartSeries {
  name: string;
  data: number[];
}

interface UniversalChartData {
  type: 'line';
  title?: string;
  subtitle?: string;
  categories: string[];
  series: ChartSeries[];
  xAxisTitle?: string;
  yAxisTitle?: string;
  showLegend?: boolean;
  colors?: string[];
  showDataLabels?: boolean;
  labelsMode?: 'all' | 'smart' | 'peaks' | 'none';
}

interface LineChartProps {
  data: UniversalChartData;
  showDataLabels?: boolean;
  labelsMode?: 'all' | 'smart' | 'peaks' | 'none';
  containerHeight?: number;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  showDataLabels = false,
  labelsMode = 'smart',
  containerHeight: propContainerHeight
}) => {
  const [isSampleData, setIsSampleData] = useState(false);
  const [showAllLegends, setShowAllLegends] = useState(false);
  const [itemsPerRow, setItemsPerRow] = useState(3);
  const [selectedSeries, setSelectedSeries] = useState<Set<number>>(new Set());
  const chartRef = useRef<any>(null);

  const colorPalette = [
    "#53eafd",
    "#ffdf20",
    "#bbf451",
    "#ffa2a2",
    "#c4b4ff",
    "#8ec5ff",
    "#ffb86a",
    "#7bf1a8",
    "#fda5d5",
    "#46ecd5",
    "#ffd230",
    "#a3b3ff",
    "#ffa1ad",
    "#5ee9b5",
    "#dab2ff",
    "#74d4ff",
    "#f4a8ff"
  ];

  // Helper function to format large numbers
  const formatValue = (value: number | string | undefined): string => {
    if (value === undefined || value === null || value === '') {
      return '0';
    }

    let numValue: number;
    if (typeof value === 'string') {
      const cleanedValue = value.replace(/[,\s]/g, '');
      numValue = parseFloat(cleanedValue);
    } else {
      numValue = Number(value);
    }

    if (isNaN(numValue) || !isFinite(numValue)) {
      return '0';
    }

    if (numValue >= 1000000000) {
      return (numValue / 1000000000).toFixed(1) + 'B';
    } else if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + 'M';
    } else if (numValue >= 1000) {
      return (numValue / 1000).toFixed(1) + 'K';
    }
    return Math.round(numValue).toString();
  };

  const calculateItemsPerRow = (seriesData: any[]) => {
    const container = document.getElementById('legend-container');
    if (!container) return;

    // Create a temporary container to measure items
    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: nowrap;
      font-family: Inter;
      font-size: 13px;
      line-height: 18px;
      letter-spacing: 0.32px;
    `;
    document.body.appendChild(tempContainer);

    let itemsThatFit = 0;
    const containerWidth = container.offsetWidth;

    // First, measure the "All" button width
    const allButton = document.createElement('div');
    allButton.style.position = 'absolute';
    allButton.style.visibility = 'hidden';
    allButton.style.whiteSpace = 'nowrap';
    allButton.style.fontFamily = 'Inter';
    allButton.style.fontWeight = '500';
    allButton.style.fontSize = '13px';
    allButton.style.lineHeight = '18px';
    allButton.style.letterSpacing = '0.32px';
    allButton.textContent = 'Select All';
    document.body.appendChild(allButton);
    const allButtonWidth = allButton.offsetWidth + 15; // 15px for gap
    document.body.removeChild(allButton);

    // Reserve space for "See More" button (assuming it will be needed)
    const seeMoreButton = document.createElement('div');
    seeMoreButton.style.position = 'absolute';
    seeMoreButton.style.visibility = 'hidden';
    seeMoreButton.style.whiteSpace = 'nowrap';
    seeMoreButton.style.fontFamily = 'Inter';
    seeMoreButton.style.fontWeight = '400';
    seeMoreButton.style.fontSize = '13px';
    seeMoreButton.style.lineHeight = '18px';
    seeMoreButton.style.letterSpacing = '0.32px';
    seeMoreButton.textContent = 'See More';
    document.body.appendChild(seeMoreButton);
    const seeMoreButtonWidth = seeMoreButton.offsetWidth + 15; // 15px for gap
    document.body.removeChild(seeMoreButton);

    // Calculate available width for legend items (reserve space for both buttons)
    const availableWidth = containerWidth - allButtonWidth - seeMoreButtonWidth;

    // Measure each legend item to see how many fit
    for (let i = 0; i < seriesData.length; i++) {
      const series = seriesData[i];
      const itemText = series.name;

      // Create a temporary item to measure
      const tempItem = document.createElement('div');
      tempItem.style.cssText = `
        display: inline-flex;
        align-items: center;
        gap: 10px;
        margin-right: 15px;
      `;

      const colorBox = document.createElement('span');
      colorBox.style.cssText = `
        width: 15px;
        height: 15px;
        border-radius: 50%;
        background-color: ${colorPalette[i % colorPalette.length]};
        flex-shrink: 0;
      `;

      const textSpan = document.createElement('span');
      textSpan.textContent = itemText;

      tempItem.appendChild(colorBox);
      tempItem.appendChild(textSpan);
      tempContainer.appendChild(tempItem);

      if (tempContainer.offsetWidth > availableWidth) {
        break;
      }

      itemsThatFit = i + 1;
    }

    document.body.removeChild(tempContainer);

    // Set items per row based on what actually fits (excluding the See more button space)
    setItemsPerRow(Math.max(1, itemsThatFit));
  };

  // Toggle series selection
  const toggleSeriesSelection = (seriesIndex: number) => {
    const allSelected = selectedSeries.size === 0 || selectedSeries.size === data.series.length;

    if (allSelected) {
      // When all are selected (or default state), clicking one selects ONLY that item
      setSelectedSeries(new Set([seriesIndex]));
    } else {
      // When some items are selected, toggle behavior
      const newSelectedSeries = new Set(selectedSeries);
      if (newSelectedSeries.has(seriesIndex)) {
        newSelectedSeries.delete(seriesIndex);
        // If nothing left selected, reset to show all
        if (newSelectedSeries.size === 0) {
          setSelectedSeries(new Set());
        } else {
          setSelectedSeries(newSelectedSeries);
        }
      } else {
        newSelectedSeries.add(seriesIndex);
        setSelectedSeries(newSelectedSeries);
      }
    }
  };

  // Select all series
  const selectAllSeries = () => {
    const allSeries = new Set(data.series.map((_, index) => index));
    setSelectedSeries(allSeries);
  };

  // Helper function to check if value is too close to x-axis (bottom 20% of chart)
  const isValueTooLowForLabel = (value: number, allValues: number[]): boolean => {
    if (allValues.length === 0) return false;

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const range = max - min;

    // If value is in bottom 20% of the range, consider it too close to x-axis
    const threshold = min + (range * 0.2);
    return value <= threshold;
  };

  // Helper functions for peak detection and alignment
  const isPeakOrValley = (index: number, dataArray: number[]) => {
    if (index === 0 || index === dataArray.length - 1) return true;

    const current = dataArray[index];
    const prev = dataArray[index - 1];
    const next = dataArray[index + 1];

    const isPeak = current > prev && current > next;
    const isValley = current < prev && current < next;

    return isPeak || isValley;
  };

  const getSmartAlignment = (index: number, dataArray: number[], allDataArrays: number[][] = []) => {
    if (index === 0 || index === dataArray.length - 1) return 'top';

    const current = dataArray[index];
    const prev = dataArray[index - 1];
    const next = dataArray[index + 1];

    // Check if value is too close to x-axis across all datasets
    const allValues = allDataArrays.flat();
    if (isValueTooLowForLabel(current, allValues)) {
      return 'top'; // Force labels upward when too close to x-axis
    }

    if ((prev === undefined || current >= prev) &&
      (next === undefined || current >= next)) {
      return 'top';
    }
    return 'bottom';
  };

  const prepareChartData = (): ChartData<'line'> => {
    const colors = data.colors || colorPalette;
    const isMultiLine = data.series.length > 1;

    const datasets = data.series.map((series, index) => ({
      label: series.name,
      data: series.data,
      borderColor: colors[index % colors.length],
      backgroundColor: colors[index % colors.length] + '20',
      borderWidth: 2,
      fill: false,
      tension: 0.1,
      pointBackgroundColor: '#FFFFFF',
      pointBorderColor: colors[index % colors.length],
      pointRadius: 1,
      pointHoverBackgroundColor: colors[index % colors.length],
      pointHoverBorderColor: '#FFFFFF',
      pointHoverRadius: 6,
      pointBorderWidth: 2,
      hidden: selectedSeries.size > 0 && !selectedSeries.has(index), // Hide if some are selected and this one is not
      // Ensure tooltip uses the solid color (without transparency)
      hoverBackgroundColor: colors[index % colors.length],
      hoverBorderColor: colors[index % colors.length]
    }));

    return {
      labels: data.categories,
      datasets: datasets
    };
  };

  const createDataLabelsConfig = () => {
    if (!showDataLabels || labelsMode === 'none') {
      return { display: false };
    }

    // Hide labels if there are more than 24 data points to prevent overcrowding
    if (data.categories.length > 24) {
      return { display: false };
    }

    // Hide labels if there are more than 5 legend items (series) to prevent overcrowding
    if (data.series.length > 5) {
      return { display: false };
    }

    const chartJSData = prepareChartData();

    // Get all values from all datasets for threshold calculation
    const allDataValues = chartJSData.datasets.flatMap(dataset => dataset.data as number[]);

    const baseConfig = {
      display: true,
      backgroundColor: 'transparent',
      borderColor: 'none',
      borderWidth: 0,
      borderRadius: 0,
      color: '#323f4a',
      font: {
        size: 10
      },
      padding: 6
    };

    if (labelsMode === 'all') {
      return {
        ...baseConfig,
        align: 'top' as const,
        offset: 10,
        display: (context: any) => {
          const value = context.dataset.data[context.dataIndex] as number;
          // Hide label if value is too close to x-axis
          return !isValueTooLowForLabel(value, allDataValues);
        },
        formatter: (value: number) => {
          return formatValue(value);
        }
      };
    }

    if (labelsMode === 'smart') {
      return {
        ...baseConfig,
        align: (context: any) => {
          const datasetIndex = context.datasetIndex;
          const dataset = chartJSData.datasets[datasetIndex];
          const dataArray = dataset.data as number[];
          return getSmartAlignment(context.dataIndex, dataArray, chartJSData.datasets.map(d => d.data as number[]));
        },
        offset: 10,
        display: (context: any) => {
          const value = context.dataset.data[context.dataIndex] as number;
          // Hide label if value is too close to x-axis
          return !isValueTooLowForLabel(value, allDataValues);
        },
        formatter: (value: number) => {
          return formatValue(value);
        }
      };
    }

    if (labelsMode === 'peaks') {
      return {
        ...baseConfig,
        display: (context: any) => {
          const datasetIndex = context.datasetIndex;
          const dataset = chartJSData.datasets[datasetIndex];
          const dataArray = dataset.data as number[];
          const value = dataArray[context.dataIndex];

          // Hide if too close to x-axis OR not a peak/valley
          const tooClose = isValueTooLowForLabel(value, allDataValues);
          const isPeakValley = isPeakOrValley(context.dataIndex, dataArray);

          return !tooClose && isPeakValley;
        },
        backgroundColor: (context: any) => {
          const datasetIndex = context.datasetIndex;
          const dataset = chartJSData.datasets[datasetIndex];
          const dataArray = dataset.data as number[];
          const index = context.dataIndex;

          if (index === 0 || index === dataArray.length - 1) {
            return 'rgba(107, 114, 128, 0.9)';
          }

          const current = dataArray[index];
          const prev = dataArray[index - 1];
          const next = dataArray[index + 1];

          if (current > prev && current > next) {
            return 'rgba(34, 197, 94, 0.9)';
          }
          if (current < prev && current < next) {
            return 'rgba(239, 68, 68, 0.9)';
          }
          return 'rgba(107, 114, 128, 0.9)';
        },
        align: (context: any) => {
          const datasetIndex = context.datasetIndex;
          const dataset = chartJSData.datasets[datasetIndex];
          const dataArray = dataset.data as number[];
          return getSmartAlignment(context.dataIndex, dataArray, chartJSData.datasets.map(d => d.data as number[]));
        },
        offset: 12,
        formatter: (value: number) => {
          return formatValue(value);
        }
      };
    }

    return { display: false };
  };

  // Calculate items per row when data changes
  useEffect(() => {
    if (data?.series && data.series.length > 0) {
      setTimeout(() => {
        calculateItemsPerRow(data.series);
      }, 100);
    }
  }, [data?.series]);

  // Recalculate on window resize
  useEffect(() => {
    const handleResize = () => {
      if (data?.series && data.series.length > 0) {
        calculateItemsPerRow(data.series);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [data?.series]);

  // Check for data after all hooks are called
  if (!data || !data.series || data.series.length === 0) {
    return <div className="text-center p-4 text-gray-500 pt-36 pb-36">No data available for chart</div>;
  }

  const chartJSData = prepareChartData();
  const isMultiLine = data.series.length > 1;

  // Chart options
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 5,
        left: 0,
        right: 0,
        top: 25
      }
    },
    plugins: {
      legend: {
        display: false, // Hide built-in legend, but keep it enabled for HTML legend plugin
        labels: {
          generateLabels: function (chart: any) {
            const chartData = chart.data;
            if (chartData.datasets && chartData.datasets.length > 0) {
              return chartData.datasets.map((dataset: any, index: number) => ({
                text: dataset.label || `Series ${index + 1}`,
                fillStyle: dataset.borderColor,
                strokeStyle: dataset.borderColor,
                lineWidth: dataset.borderWidth || 2,
                hidden: !chart.isDatasetVisible(index),
                datasetIndex: index
              }));
            }
            return [];
          }
        }
      },
      title: {
        display: false, // Disable built-in title, use HTML title instead
      },
      tooltip: {
        displayColors: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: colorPalette[0],
        borderWidth: 1,
        filter: function (tooltipItem: any) {
          return tooltipItem.parsed.y > 0;
        },
        mode: 'nearest',
        usePointStyle: true, // This forces circles instead of squares
        callbacks: {
          title: function (context: any[]) {
            return context[0]?.label || '';
          },
          label: function (context: any) {
            const value = context.parsed.y;
            if (value === 0) return '';

            const formattedValue = formatValue(value);

            if (isMultiLine) {
              return `${context.dataset.label}: ${formattedValue}`;
            } else {
              return `${data.yAxisTitle || 'Value'}: ${formattedValue}`;
            }
          },
          labelColor: function (context: any) {
            // Return the correct color for the tooltip circle (filled)
            const datasetIndex = context.datasetIndex;
            const colorIndex = datasetIndex % colorPalette.length;
            const seriesColor = colorPalette[colorIndex];

            return {
              borderColor: seriesColor,
              backgroundColor: seriesColor // Fill the entire circle with legend color
            };
          }
        }
      },
      datalabels: createDataLabelsConfig()
    },
    scales: {
      x: {
        title: {
          display: !!data.xAxisTitle,
          text: data.xAxisTitle,
          font: {
            size: 13,
            lineHeight: 1.8
          }
        },
        grid: {
          display: false
        },
        ticks: {
          color: '#424242',
          maxTicksLimit: undefined, // Remove the limit completely
          autoSkip: true,
          maxRotation: 45,
          minRotation: 0,
          font: {
            size: propContainerHeight && propContainerHeight > 500 ? 11 : 10
          },
          callback: function (value, index) {
            const label = this.getLabelForValue(value as number);

            // For large datasets, show labels more frequently to cover the timeline
            if (chartJSData.labels && chartJSData.labels.length > 100) {
              // Show every nth label to get about 10-12 labels total across the timeline
              const step = Math.ceil(chartJSData.labels.length / 12);
              return index % step === 0 ? label : '';
            } else if (chartJSData.labels && chartJSData.labels.length > 50) {
              // Show every 5th label for medium-large datasets
              return index % 5 === 0 ? label : '';
            } else if (chartJSData.labels && chartJSData.labels.length > 20) {
              // Show every 3rd label for medium datasets
              return index % 3 === 0 ? label : '';
            } else if (chartJSData.labels && chartJSData.labels.length > 12) {
              // Show every 2nd label for datasets with more than 12 labels
              return index % 2 === 0 ? label : '';
            }

            // For 12 or fewer labels, show all labels
            return label;
          }
        }
      },
      y: {
        title: {
          display: !!data.yAxisTitle,
          text: data.yAxisTitle,
          font: {
            size: 13,
            lineHeight: 1.8
          }
        },
        beginAtZero: true,
        grid: {
          color: '#E2E8F0',
        },
        ticks: {
          color: '#424242',
          font: {
            size: propContainerHeight && propContainerHeight > 500 ? 11 : 10
          },
          callback: function (value) {
            return formatValue(Number(value));
          }
        }
      }
    },
    elements: {
      point: {
        hoverRadius: 6,
        radius: 3,
        borderWidth: 0
      }
    },
    interaction: {
      intersect: true,
      mode: 'nearest'
    }
  };

  return (
    <>
      <div className="w-full">
        {/* Chart Title */}
        {data.title && (
          <div className="text-h4 text-primary-text mt-2 mb-4 text-left">{data.title}</div>
        )}
        {/* React Legend Container - After title, before chart */}
        {data.showLegend !== false && (
          <div id="legend-container" className="mb-4">
            <ul key={showAllLegends ? 'expanded' : 'collapsed'} className="flex" style={{
              flexWrap: showAllLegends ? 'wrap' : 'nowrap',
              overflow: showAllLegends ? 'visible' : 'hidden',
              gap: '8px'
            }}>
              {(() => {
                const itemsToShow = showAllLegends ? data.series.length : itemsPerRow;
                const hasMoreItems = data.series.length > itemsPerRow;
                const hasSelectedItems = selectedSeries.size > 0;
                const allItemsSelected = selectedSeries.size === data.series.length;
                const shouldShowSelectAll = hasSelectedItems && !allItemsSelected;
                // If nothing is selected, treat all as selected (default state)
                const effectiveSelectedSeries = selectedSeries.size > 0 ? selectedSeries : new Set(data.series.map((_, index) => index));

                return (
                  <>
                    {/* "Select All" option - only show when some (but not all) items are selected */}
                    {shouldShowSelectAll && (
                      <li
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={selectAllSeries}
                      >
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0 border-2"
                          style={{
                            backgroundColor: 'transparent',
                            borderColor: '#4F46E5',
                            borderRadius: '50%'
                          }}
                        />
                        <span
                          className="text-sm font-medium whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '500',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: '#4F46E5'
                          }}
                        >
                          Select All
                        </span>
                      </li>
                    )}

                    {data.series.slice(0, itemsToShow).map((series, index) => (
                      <li
                        key={index}
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={() => toggleSeriesSelection(index)}
                      >
                        <span
                          className="w-4 h-4 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: colorPalette[index % colorPalette.length],
                            borderRadius: '50%',
                            opacity: effectiveSelectedSeries.has(index) ? 1 : 0.5
                          }}
                        />
                        <span
                          className="text-sm whitespace-nowrap"
                          style={{
                            fontFamily: 'Inter',
                            fontWeight: '400',
                            fontSize: '13px',
                            lineHeight: '18px',
                            letterSpacing: '0.32px',
                            color: effectiveSelectedSeries.has(index) ? '#323f4a' : '#aaa',
                            textDecoration: 'none'
                          }}
                        >
                          {series.name}
                        </span>
                      </li>
                    ))}
                    {hasMoreItems && !showAllLegends && (
                      <li
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(true)}
                      >
                        <span>See more ({data.series.length - itemsPerRow})</span>
                        <span style={{ fontSize: '10px' }}>▼</span>
                      </li>
                    )}
                    {hasMoreItems && showAllLegends && (
                      <li
                        className="flex items-center gap-1 cursor-pointer px-2 rounded-full text-sm transition-colors"
                        style={{
                          color: '#6b7280',
                          fontFamily: 'Inter',
                          backgroundColor: 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e6e6e6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        onClick={() => setShowAllLegends(false)}
                      >
                        <span>See less</span>
                        <span style={{ fontSize: '10px' }}>▲</span>
                      </li>
                    )}
                  </>
                );
              })()}
            </ul>
          </div>
        )}
        <div className="w-full" style={{ height: `${propContainerHeight || 400}px` }}>
          <Line
            ref={chartRef}
            data={chartJSData}
            options={options}
          />
        </div>
      </div>
    </>
  );
};

export default LineChart;